import { findIndex, findLastIndex } from 'lodash';
import { toast } from 'react-toastify';

import { findMinBy, mapGroupBy } from '@seaber/common/lib/collections';
import type { LocationId, TCOrderEntry } from '@seaber/common/core/entries';

import {
  assertDefined,
  assertedDefined,
  get,
  isDefined,
} from '../../../lib/util';
import type { ScenarioOrder } from '../../../lib/planningData';
import type { Order } from '../../../graphql';
import type { ViolationIndicatorBasis } from '../../../state/planning/options';
import type { VoyagePlacement } from '../../timeline/ScenarioTimeline';
import type { useSpecialRequirements } from '../../hooks/useSpecialRequirements';

import type { Vessel } from '../Vessel';
import type {
  ScheduleDataContext,
  CalculationConfig,
} from '../ScheduleDataContext';
import type { VesselSchedule } from '../VesselSchedule';
import type { ClientId } from '../ClientId';
import type {
  TCOrderEvent,
  TCTimelineEvent,
  TimeData,
  LoadEvent,
  DischargeEvent,
  SingleEvent,
} from '../TimeData';
import {
  isCargoEvent,
  isPortCallArrivalEvent,
  isPortCallBerthingEventTimeData,
  isTCOrderEvent,
  isLoadEvent,
  getEventStartLocationId,
} from '../TimeData';
import type { CargoAllocationOptions } from '../suggestions/Suggestions';
import {
  CreateScheduleEntry,
  CreateSingleEntry,
  deriveBestTCSchedule,
  orderFullfillsSpecialRequirements,
  singleEntryToSingleEvent,
  tcOrderEntryToTcOrderEvents,
} from '../Core';
import type { CargoCompartment } from '../Core.cargo-allocation';
import {
  getCompartments,
  isCorrectCargoAllocationType,
} from '../Core.cargo-allocation';
export type NewTcAssignment = {
  order: ScenarioOrder;
  shared?: boolean;
  params: Partial<TCOrderEntry>;
};

export type PlacementBounds = {
  minimumIndex: number;
  maximumIndex: number;
  shipmentId: ClientId | null;
  /** Index at which to reset arrival durations, or null if not needed */
  arrivalResetIndex: number | null;
};

export function computePlacementBounds(
  originalEvents: VesselSchedule,
  originalTimeData: TimeData[],
  start: number,
  shipWithOrderId: Order['id'] | null,
): PlacementBounds {
  let minimumIndex = originalTimeData.findIndex(
    (event) => +event.start > start,
  );
  if (minimumIndex === -1) minimumIndex = originalEvents.length;

  if (shipWithOrderId) {
    const orderEvent = originalEvents
      .filter(isTCOrderEvent)
      .find((e) => e.order.id === shipWithOrderId);
    assertDefined(orderEvent, `Expected to find event to ship with`);
    const shipmentId = orderEvent.order.shipmentId;

    const matchShipmentEvent =
      shipmentId === null
        ? (e: TCTimelineEvent) =>
            isCargoEvent(e) && e.order.id === orderEvent.order.id
        : (e: TCTimelineEvent) =>
            isCargoEvent(e) && e.order.shipmentId === shipmentId;

    minimumIndex = Math.max(
      minimumIndex,
      findIndex(originalEvents, matchShipmentEvent),
    );
    const maximumIndex = Math.max(
      minimumIndex,
      findLastIndex(originalEvents, matchShipmentEvent) + 1,
    );

    let arrivalResetIndex = minimumIndex;
    while (
      originalEvents[arrivalResetIndex]?.portCallId === orderEvent.portCallId &&
      !isPortCallArrivalEvent(originalEvents[arrivalResetIndex]) &&
      arrivalResetIndex > 0
    ) {
      arrivalResetIndex--;
    }

    return { minimumIndex, maximumIndex, shipmentId, arrivalResetIndex };
  }

  // When dropping new assignments onto timeline before a shipment "box", often the arrival event already happened earlier because drawing begins from berthing.
  // So if we dropped between arrival and berthing, take one step to the left so we position before the arrival.
  const tdAfter = get(originalTimeData, minimumIndex);
  if (tdAfter && isPortCallBerthingEventTimeData(tdAfter)) {
    minimumIndex = Math.max(0, minimumIndex - 1);
  }

  return {
    minimumIndex,
    maximumIndex: minimumIndex,
    shipmentId: null,
    arrivalResetIndex: null,
  };
}

type GreedyScheduleResult = {
  schedule: TCTimelineEvent[];
  maximumIndex: number;
  firstChangedIndex: number;
  objectiveScore: number;
};

const portPairKey = ([load, discharge]: [
  LoadEvent,
  DischargeEvent,
]): `${LocationId}-${LocationId}` =>
  `${load.locationId}-${discharge.locationId}`;

/** Place a single load-discharge event pair onto a schedule. */
function placeOneEventPair(
  pair: [LoadEvent & TCOrderEvent, DischargeEvent & TCOrderEvent],
  schedule: TCTimelineEvent[],
  context: ScheduleDataContext,
  config: CalculationConfig,
  minimumIndex: number,
  maximumIndex: number,
  allocationOptions: CargoAllocationOptions,
  shipmentId: ClientId | null,
  allAvailableOrderIds: Set<number>,
  violationBasis: ViolationIndicatorBasis,
  exactStart: number | null,
  newOrderIds: ReadonlySet<number>,
): GreedyScheduleResult | null {
  const [load, discharge] = pair;
  const bestSchedule = deriveBestTCSchedule(
    { load, discharge },
    schedule,
    context,
    config,
    {
      minimumIndex,
      maximumIndex,
      tryOverloadingIfNoValidFound: true,
      allocationOptions,
      shipmentId,
      exactStart,
      newOrderIds,
    },
    allAvailableOrderIds,
    violationBasis,
  );

  if (!bestSchedule) return null;

  const { combination, objectiveScore } = bestSchedule;
  const indices = combination.assignments[0];
  // Shift maximumIndex by schedule growth to the right, to provide the same event based bounds to all orders regardless of place in sequence
  const scheduleGrowth = combination.schedule.length - schedule.length;
  return {
    schedule: combination.schedule,
    maximumIndex: Math.max(
      maximumIndex + scheduleGrowth,
      indices.dischargeIndex + 1,
    ),
    firstChangedIndex: indices.loadIndex,
    objectiveScore,
  };
}

export type PortPairKey = `${LocationId}-${LocationId}`;

const portPairKeyToLocationIds = (
  pairKey: PortPairKey,
): [LocationId, LocationId] => {
  const [load, discharge] = pairKey.split('-').map((s) => +s as LocationId);
  return [load, discharge];
};

/** Count how many of a pair's ports already appear in placed pairs (0 = both novel, 2 = both seen). */
function portNoveltyScore(
  pairKey: PortPairKey,
  usedPorts: Set<LocationId>,
): number {
  const [load, discharge] = portPairKeyToLocationIds(pairKey);
  return (usedPorts.has(load) ? 1 : 0) + (usedPorts.has(discharge) ? 1 : 0);
}

function extractUsedPorts(pairKeys: PortPairKey[]): Set<LocationId> {
  const ports = new Set<LocationId>();
  for (const key of pairKeys) {
    const [load, discharge] = portPairKeyToLocationIds(key);
    ports.add(load);
    ports.add(discharge);
  }
  return ports;
}

/**
 * Returns the port pair keys available for the next placement step.
 * Diversity constraint: untried port pairs must be exhausted before repeating.
 * Within the allowed set, sorted by port novelty: prefer pairs visiting ports
 * not yet seen in placed pairs (0 = both new > 1 = one new > 2 = both seen).
 */
export function getNextPortPairKeys(
  remainingKeys: PortPairKey[],
  placedKeys: PortPairKey[],
): PortPairKey[] {
  const placed = new Set(placedKeys);
  const uniqueRemaining = [...new Set(remainingKeys)];
  const untried = uniqueRemaining.filter((k) => !placed.has(k));
  const candidates = untried.length > 0 ? untried : uniqueRemaining;

  const usedPorts = extractUsedPorts(placedKeys);
  return candidates
    .slice()
    .sort(
      (a, b) => portNoveltyScore(a, usedPorts) - portNoveltyScore(b, usedPorts),
    );
}

/** Empirically chosen to balance search quality vs. computation cost.
 * Higher values find better sequences but scale the work per level linearly. */
const BEAM_WIDTH = 3;

/** Extract the port rotation for the whole schedule */
export function getPortRotation(schedule: TCTimelineEvent[]): number[] {
  const ports: number[] = [];
  let prevLocation: number | null = null;
  for (const e of schedule) {
    const location = getEventStartLocationId(e);
    if (location !== prevLocation) {
      ports.push(location);
      prevLocation = location;
    }
  }
  return ports;
}

type BeamState = {
  schedule: TCTimelineEvent[];
  maximumIndex: number;
  firstChangedIndex: number;
  placedPortPairKeys: PortPairKey[];
  placedOrderIds: number[];
  remainingEventPairs: [
    LoadEvent & TCOrderEvent,
    DischargeEvent & TCOrderEvent,
  ][];
  objectiveScore: number;
};

/**
 * Beam search over order assignment orderings.
 * At each level, expands beams by trying each available port pair (largest order
 * per pair first), keeping the top-scoring beams. Once all distinct port pairs
 * have been placed, falls back to greedy (beam width 1).
 */
function beamSearchAssignment(
  eventPairs: [LoadEvent & TCOrderEvent, DischargeEvent & TCOrderEvent][],
  existingEvents: TCTimelineEvent[],
  context: ScheduleDataContext,
  config: CalculationConfig,
  minimumIndex: number,
  maximumIndex: number,
  allocationOptions: CargoAllocationOptions,
  shipmentId: ClientId | null,
  allAvailableOrderIds: Set<number>,
  violationBasis: ViolationIndicatorBasis,
  /** Non-null when the first event of the new voyage should start at an exact time. */
  exactStart: number | null,
): GreedyScheduleResult | null {
  const distinctPairs = new Set(eventPairs.map(portPairKey));

  let beams: BeamState[] = [
    {
      schedule: existingEvents.slice(),
      maximumIndex,
      firstChangedIndex: Infinity,
      placedPortPairKeys: [],
      placedOrderIds: [],
      remainingEventPairs: eventPairs,
      objectiveScore: 0,
    },
  ];

  for (let level = 0; level < eventPairs.length; level++) {
    // Explore multiple orderings while untried port pairs remain (beam width > 1).
    // Once every distinct pair has been placed at least once, the remaining orders
    // share a port pair with one already placed, so ordering matters less — fall
    // back to greedy (beam width 1) for faster execution.
    const beamWidth = level < distinctPairs.size ? BEAM_WIDTH : 1;
    const candidates: BeamState[] = [];

    for (const beam of beams) {
      const eventPairsByPortPair = mapGroupBy(
        beam.remainingEventPairs,
        portPairKey,
      );

      const allowedPortPairs = getNextPortPairKeys(
        [...eventPairsByPortPair.keys()],
        beam.placedPortPairKeys,
      );
      // In greedy mode, only try one pair — we'll insert all orders eventually anyway
      const portPairsToTry =
        beamWidth === 1 ? allowedPortPairs.slice(0, 1) : allowedPortPairs;

      for (const portPair of portPairsToTry) {
        const group = eventPairsByPortPair.get(portPair);
        if (!group || group.length === 0) continue;

        // Try the largest order first — it constrains the schedule the most
        const eventPair = group.reduce((a, b) =>
          a[0].order.cargoRanges.mass.min >= b[0].order.cargoRanges.mass.min
            ? a
            : b,
        );

        const newOrderIds = new Set([
          ...beam.placedOrderIds,
          eventPair[0].order.id,
        ]);

        const result = placeOneEventPair(
          eventPair,
          beam.schedule,
          context,
          config,
          minimumIndex,
          beam.maximumIndex,
          allocationOptions,
          shipmentId,
          allAvailableOrderIds,
          violationBasis,
          exactStart,
          newOrderIds,
        );
        if (!result) continue;

        const candidate: BeamState = {
          schedule: result.schedule,
          maximumIndex: result.maximumIndex,
          firstChangedIndex: Math.min(
            result.firstChangedIndex,
            beam.firstChangedIndex,
          ),
          placedPortPairKeys: [...beam.placedPortPairKeys, portPair],
          placedOrderIds: [...beam.placedOrderIds, eventPair[0].order.id],
          remainingEventPairs: beam.remainingEventPairs.filter(
            (p) => p !== eventPair,
          ),
          objectiveScore: result.objectiveScore,
        };
        candidates.push(candidate);
      }
    }

    if (candidates.length === 0) break;

    // Dedup beams that produced the same port rotation and would waste beam slots.
    const byDedupKey = new Map<string, BeamState>();
    for (const candidate of candidates) {
      const dedupKey = getPortRotation(candidate.schedule).join(',');
      const existing = byDedupKey.get(dedupKey);
      if (!existing || candidate.objectiveScore < existing.objectiveScore) {
        byDedupKey.set(dedupKey, candidate);
      }
    }

    beams = [...byDedupKey.values()]
      .sort((a, b) => a.objectiveScore - b.objectiveScore)
      .slice(0, beamWidth);
  }

  const completedBeams = beams.filter(
    (b) => b.remainingEventPairs.length === 0,
  );
  // findMinBy returns undefined when all scores are NaN (e.g. missing distances);
  // fall back to first completed beam since any valid placement is acceptable.
  const best =
    findMinBy(completedBeams, (b) => b.objectiveScore) ?? completedBeams[0];
  if (!best) return null;

  return {
    schedule: best.schedule,
    maximumIndex: best.maximumIndex,
    firstChangedIndex: best.firstChangedIndex,
    objectiveScore: best.objectiveScore,
  };
}

function createEventPairsForTcAssignment(
  assignments: NewTcAssignment[],
  originalEvents: VesselSchedule,
  vessel: Vessel,
  start: number,
  context: ScheduleDataContext,
  config: CalculationConfig,
  voyagePlacement: VoyagePlacement,
  specialRequirements: ReturnType<typeof useSpecialRequirements>,
): [LoadEvent & TCOrderEvent, DischargeEvent & TCOrderEvent][] | null {
  const entries = assignments
    .map((assignment, i) => {
      const order = assignment.order;

      const { isFulfilled: specialRequirementFulfilled } =
        orderFullfillsSpecialRequirements(vessel, order, specialRequirements);

      if (!specialRequirementFulfilled) {
        toast.warn(
          `The vessel ${vessel.name} does not satisfy all order special requirements for order ${order.id}. Assignment created with warning.`,
        );
      }

      const scheduleEntry = CreateScheduleEntry(
        originalEvents,
        order,
        vessel,
        start + i,
        context,
        config,
        assignment.params,
      );

      return (
        scheduleEntry && {
          ...scheduleEntry,
          shared: assignment.shared ?? false,
        }
      );
    })
    .filter(isDefined);

  if (entries.length !== assignments.length) {
    toast.error(
      `At least one of the assignments was invalid. No assignments were created.`,
    );
    return null;
  }

  return entries
    .map((entry, i) => {
      const assignment = assertedDefined(
        assignments[i],
        `Expected assignment at index ${i}`,
      );
      return tcOrderEntryToTcOrderEvents(
        entry,
        vessel,
        assignment.order,
        context.locations,
        {
          isNew: true,
          waitForLaycan: voyagePlacement === 'laycan',
        },
      );
    })
    .filter(isDefined);
}

/** Assign orders using beam search over placement orderings, optimizing for shortest total distance. */
export function assignOrders(
  assignments: NewTcAssignment[],
  originalEvents: VesselSchedule,
  existingEvents: TCTimelineEvent[],
  vessel: Vessel,
  start: number,
  context: ScheduleDataContext,
  config: CalculationConfig,
  minimumIndex: number,
  maximumIndex: number,
  allocationOptions: CargoAllocationOptions,
  voyagePlacement: VoyagePlacement,
  shipmentId: ClientId | null,
  allAvailableOrderIds: Set<number>,
  violationBasis: ViolationIndicatorBasis,
  specialRequirements: ReturnType<typeof useSpecialRequirements>,
): GreedyScheduleResult | null {
  const createdEventPairs = createEventPairsForTcAssignment(
    assignments,
    originalEvents,
    vessel,
    start,
    context,
    config,
    voyagePlacement,
    specialRequirements,
  );

  if (!createdEventPairs) {
    return null;
  }

  const exactStart = voyagePlacement === 'exact' ? start : null;

  const result = beamSearchAssignment(
    createdEventPairs,
    existingEvents,
    context,
    config,
    minimumIndex,
    maximumIndex,
    allocationOptions,
    shipmentId,
    allAvailableOrderIds,
    violationBasis,
    exactStart,
  );

  if (!result) {
    toast.error(
      'Could not find a valid position for the orders. They may be too large for the given position.',
    );
  }

  return result;
}

export type SingleEventsInfo = Array<{
  singleEvents: SingleEvent[];
  preTCEvent: LoadEvent | DischargeEvent;
  nextTCEvent: LoadEvent | DischargeEvent;
}>;

/**
 * Preserves single events (off-hires, waits) between TC order events when moving them.
 *
 * When TC orders are moved with single events between them, those single events need
 * to be re-inserted at the new location. This function:
 * 1. Sorts the newly placed TC events chronologically
 * 2. Finds where the pre/next TC events landed in the schedule
 * 3. If they're adjacent (as expected after assignment), inserts the single events between them
 *
 * This maintains the user's intended event structure when repositioning orders.
 */
export function insertSingleEvents(
  schedule: TCTimelineEvent[],
  singleEventsInfo: SingleEventsInfo,
  allAvailableOrderIds: Set<number>,
  start: number,
  vessel: Vessel,
  context: ScheduleDataContext,
): TCTimelineEvent[] | null {
  const newAllEvents = schedule.slice();

  // Keep the TC port calls preserved
  const minIdx = newAllEvents.findIndex(
    (e) => isCargoEvent(e) && allAvailableOrderIds.has(e.order.id),
  );
  const maxIdx = newAllEvents.findLastIndex(
    (e) => isCargoEvent(e) && allAvailableOrderIds.has(e.order.id),
  );
  const addedEvents = newAllEvents
    .slice(minIdx, maxIdx + 1)
    .sort((a, b) => a.start - b.start);
  newAllEvents.splice(minIdx, addedEvents.length, ...addedEvents);

  // Handle add single event
  for (const eventData of singleEventsInfo) {
    const {
      preTCEvent: beforePreTCEvent,
      nextTCEvent: beforeNextTCEvent,
      singleEvents,
    } = eventData;
    const afterPreIdx = newAllEvents.findIndex(
      (e) =>
        isTCOrderEvent(e) &&
        e.type === beforePreTCEvent.type &&
        e.locationId === beforePreTCEvent.locationId &&
        e.entry.orderId === beforePreTCEvent.entry.orderId,
    );
    const afterNextIdx = newAllEvents.findIndex(
      (e) =>
        isTCOrderEvent(e) &&
        e.type === beforeNextTCEvent.type &&
        e.locationId === beforeNextTCEvent.locationId &&
        e.entry.orderId === beforeNextTCEvent.entry.orderId,
    );
    const shouldInsertSingleEvent = afterPreIdx + 1 === afterNextIdx;
    if (shouldInsertSingleEvent) {
      for (const [idx, se] of singleEvents.entries()) {
        const singleEntry = CreateSingleEntry({
          type: se.entry.type,
          start: start + idx,
          duration: se.duration,
          locationId: se.locationId,
        });
        const location = context.locations.get(se.locationId);

        if (!location || afterNextIdx === -1) {
          return null;
        }
        const newSingleEvent = singleEntryToSingleEvent(
          singleEntry,
          vessel,
          location,
        );
        newAllEvents.splice(afterNextIdx + idx, 0, newSingleEvent);
      }
    }
  }

  return newAllEvents;
}

/**
 * Validates cargo allocations for newly assigned orders.
 * Shows a warning toast if allocations are missing when other events have them.
 * This indicates potential cargo type mismatches or allocation failures.
 */
export function validateCargoAllocations(
  schedule: TCTimelineEvent[],
  assignments: NewTcAssignment[],
  vessel: Vessel,
): void {
  const timelineHasCargoAllocations = schedule.some(
    (e) => isTCOrderEvent(e) && e.cargoAllocations.some((ca) => +ca.amount > 0),
  );

  const newLoadsMissingAllocations = timelineHasCargoAllocations
    ? schedule
        .filter(isTCOrderEvent)
        .filter(isLoadEvent)
        .filter(
          (e) =>
            !!assignments.find((a) => a.order.id === e.order.id) &&
            e.cargoAllocations.every((ca) => +ca.amount === 0),
        )
    : [];

  if (newLoadsMissingAllocations.length > 0) {
    const compartments = getCompartments(vessel);
    const isCargoTypeMismatch = newLoadsMissingAllocations.some((e) => {
      const cargoType = e.order.assortmentByAssortmentId?.cargoType;
      return (
        !!cargoType &&
        !compartments.some((c: CargoCompartment) =>
          isCorrectCargoAllocationType(cargoType, c),
        )
      );
    });
    toast.warn(
      `Some of the new orders could not be allocated to tanks: ${newLoadsMissingAllocations
        .map((e) => e.order.id)
        .join(',')}` + (isCargoTypeMismatch ? ' (mismatching cargo type)' : ''),
    );
  }
}
